import {IconButton} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {OpenLine} from '@dropbox/dig-icons/assets';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {displayName, getSafeAppLaunchUrl} from 'views/apps/appUtils';
import {toAppTileId, usePinnedApps} from 'views/home/dashboard/pinnedApps';
import {Tile} from 'views/home/dashboard/Tile';
import {usePinnedAppsCatalog} from 'views/home/dashboard/tiles/usePinnedAppsCatalog';

/**
 * The CompanyOS wrapper is trusted platform code on its own origin: it needs
 * scripts + its own origin (bridge fetch, ALB session) and opens links in new
 * tabs. Publisher content stays sandboxed inside the wrapper's own frame.
 */
const WRAPPER_SANDBOX =
  'allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox';

/** A pinned App Platform app, rendered live in a dashboard tile. */
export const AppEmbedTile = ({slug}: {slug: string}) => {
  const {employee} = useAtomValue(loggedInEmployeeAtom);
  const {slugs} = usePinnedApps(employee?.user_id);
  // Shared query across every app tile, so the catalog is paged once.
  const {data: apps, isLoading} = usePinnedAppsCatalog(slugs);
  const bodyRef = useSkeletonFadeIn(isLoading);
  const tileId = toAppTileId(slug);
  const name = displayName(slug);
  const app = apps?.find((candidate) => candidate.slug === slug);
  const launchUrl = app ? getSafeAppLaunchUrl(app) : null;

  if (isLoading) {
    return (
      <Tile tileId={tileId}>
        <Skeleton.Box style={{width: '100%', height: '100%'}} />
      </Tile>
    );
  }

  if (!launchUrl) {
    return (
      <Tile tileId={tileId} bodyRef={bodyRef}>
        <Text color="subtle" size="small">
          {t('dashboard_app_tile_unavailable')}
        </Text>
      </Tile>
    );
  }

  return (
    <Tile
      tileId={tileId}
      bodyRef={bodyRef}
      accessory={
        <Tooltip title={t('dashboard_app_tile_open')}>
          <IconButton
            href={launchUrl}
            target="_blank"
            rel="noopener noreferrer"
            variant="borderless"
            size="small"
            aria-label={t('dashboard_app_tile_open_aria', {name})}
          >
            <UIIcon src={OpenLine} />
          </IconButton>
        </Tooltip>
      }
    >
      <Box height="100%" borderRadius="Medium" overflow="hidden">
        <iframe
          title={name}
          src={launchUrl}
          sandbox={WRAPPER_SANDBOX}
          loading="lazy"
          style={{display: 'block', width: '100%', height: '100%', border: 0}}
        />
      </Box>
    </Tile>
  );
};
